import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { debug } from '../lib/debug.js';
import LanguageSelector from '../components/LanguageSelector.jsx';
import { saveExists, deleteSave, loadGame } from '../lib/saveManager';
import { useWorldState } from '../components/state/WorldStateContext';
import { useAuth } from '../components/auth/AuthContext';
import AuthModal from '../components/auth/AuthModal';

// ============================================================================
// MAIN MENU - Minimalist, striking design
// ============================================================================

export default function MainMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setWorldState, resetWorldState, setContinuedFromMenu } = useWorldState();
  const { user } = useAuth();
  const [showContent, setShowContent] = useState(false);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [hasExistingSave, setHasExistingSave] = useState(false);
  const [checkingSave, setCheckingSave] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Check if player has existing save file
  useEffect(() => {
    async function checkSaveFile() {
      try {
        const exists = await saveExists();
        debug('[MainMenu] Save file exists:', exists);
        setHasExistingSave(exists);
      } catch (err) {
        console.error('[MainMenu] Error checking save:', err);
        // Fallback to localStorage check
        setHasExistingSave(localStorage.getItem('playerAirline') !== null);
      }
      setCheckingSave(false);
    }
    checkSaveFile();
  }, []);

  useEffect(() => {
    // Fade in content after mount
    const timer = setTimeout(() => setShowContent(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleNewGame = async () => {
    // Clear file-based save
    try {
      await deleteSave();
      debug('[MainMenu] Deleted save file for new game');
    } catch (err) {
      console.warn('[MainMenu] Could not delete save file:', err);
    }

    // Clear any existing localStorage data for fresh start
    localStorage.removeItem('playerAirline');
    localStorage.removeItem('modeSelected');
    localStorage.removeItem('worldState');
    localStorage.removeItem('airlineOpsWorldState');
    localStorage.removeItem('gameMode');
    localStorage.removeItem('hasSeenIntro'); // Show intro for new games
    localStorage.removeItem('airlineOpsRadioEnabled'); // Radio starts after onboarding

    // Reset world state to default before navigating
    if (resetWorldState) {
      resetWorldState();
    }

    // Set flag to tell WorldStateContext this is a new game - reset day to 1
    localStorage.setItem('isNewGame', 'true');
    // Show intro sequence first, then mode selection
    navigate('/intro');
  };

  const handleContinue = async () => {
    setIsLoading(true);
    try {
      debug('[MainMenu] Continue clicked - loading save...');
      // CRITICAL FIX: Clear isNewGame flag so WorldStateContext doesn't start fresh
      localStorage.removeItem('isNewGame');
      // Mark that Continue was clicked so compensation runs AFTER this load
      setContinuedFromMenu(true);
      const result = await loadGame();

      if (result.success && result.exists && result.data) {
        debug('[MainMenu] Save loaded successfully, applying state...');
        setWorldState(prev => ({
          ...prev,
          ...result.data,
          gameSpeed: 1,
          isPaused: false,
        }));
        navigate('/OpsCenter');
      } else {
        console.error('[MainMenu] Failed to load save:', result);
        // DO NOT NAVIGATE - This was causing save corruption!
        alert('Failed to load save file. Please try again or start a new game.');
        // Stay on main menu
      }
    } catch (err) {
      console.error('[MainMenu] Error loading save:', err);
      navigate('/OpsCenter');
    } finally {
      setIsLoading(false);
    }
  };

  // Backup recovery removed — web version uses cloud saves (Phase 2)

  return (
    <div className="fixed inset-0 bg-black overflow-hidden">
      {/* Animated gradient background */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(20, 30, 48, 1) 0%, rgba(0, 0, 0, 1) 70%)',
        }}
      />

      {/* Subtle animated flight paths */}
      <svg
        className="absolute inset-0 w-full h-full opacity-10"
        style={{ filter: 'blur(1px)' }}
      >
        <defs>
          <linearGradient id="pathGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="transparent" />
            <stop offset="50%" stopColor="#60ffd0" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
        </defs>
        {/* Curved flight paths */}
        <path
          d="M-100,300 Q400,100 900,400 T1900,200"
          stroke="url(#pathGradient)"
          strokeWidth="1"
          fill="none"
          className="animate-pulse"
        />
        <path
          d="M-100,500 Q600,300 1100,600 T2100,400"
          stroke="url(#pathGradient)"
          strokeWidth="1"
          fill="none"
          className="animate-pulse"
          style={{ animationDelay: '1s' }}
        />
        <path
          d="M-100,700 Q300,500 800,700 T1800,500"
          stroke="url(#pathGradient)"
          strokeWidth="1"
          fill="none"
          className="animate-pulse"
          style={{ animationDelay: '2s' }}
        />
      </svg>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${10 + Math.random() * 20}s linear infinite`,
              animationDelay: `${Math.random() * 10}s`,
            }}
          />
        ))}
      </div>

      {/* Main content */}
      <div
        className={`relative z-10 h-full flex flex-col items-center justify-center transition-all duration-1000 ${
          showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Logo / Title */}
        <div className="mb-16 text-center">
          <h1
            className="text-6xl md:text-8xl font-extralight tracking-[0.3em] text-white mb-4"
            style={{
              textShadow: '0 0 60px rgba(96, 255, 208, 0.3), 0 0 120px rgba(96, 255, 208, 0.1)',
            }}
          >
            {t('menu.title')}
          </h1>
          <div
            className="text-2xl md:text-3xl font-extralight tracking-[0.5em] text-cyan-400/80"
            style={{
              textShadow: '0 0 30px rgba(96, 255, 208, 0.5)',
            }}
          >
            {t('menu.subtitle')}
          </div>
        </div>

        {/* Menu buttons */}
        <div className="flex flex-col gap-4 w-64">
          {/* Continue button - only show if save exists */}
          {!checkingSave && hasExistingSave && (
            <>
              <MenuButton
                onClick={handleContinue}
                isHovered={hoveredButton === 'continue'}
                onHover={() => setHoveredButton('continue')}
                onLeave={() => setHoveredButton(null)}
                primary
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : t('menu.continue')}
              </MenuButton>

            </>
          )}

          {/* New Game button */}
          <MenuButton
            onClick={handleNewGame}
            isHovered={hoveredButton === 'new'}
            onHover={() => setHoveredButton('new')}
            onLeave={() => setHoveredButton(null)}
            primary={!hasExistingSave}
          >
            {t('menu.newGame')}
          </MenuButton>

        </div>

        {/* Version / subtle footer */}
        <div className="absolute bottom-8 flex items-center gap-6 text-gray-600 text-xs tracking-widest">
          <span>v0.1.0</span>
          <a
            href="https://discord.gg/SVkBsn688z"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 hover:text-indigo-400 transition-colors duration-200"
            style={{ textDecoration: 'none' }}
          >
            DISCORD
          </a>
        </div>

        {/* Language selector */}
        <div className="absolute top-6 right-6">
          <LanguageSelector />
        </div>

        {/* Account status */}
        <div className="absolute top-6 left-6">
          {user?.isAnonymous ? (
            <button
              onClick={() => setShowAuthModal(true)}
              className="text-xs tracking-widest text-gray-500 hover:text-cyan-400 transition-colors duration-200"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              GUEST &middot; SIGN IN
            </button>
          ) : user?.email ? (
            <div className="text-xs tracking-widest text-gray-600">
              {user.email}
            </div>
          ) : null}
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={user?.isAnonymous ? 'link' : 'sign-in'}
      />

      {/* CSS for floating animation */}
      <style>{`
        @keyframes float {
          0% {
            transform: translateY(100vh) translateX(0);
            opacity: 0;
          }
          10% {
            opacity: 0.5;
          }
          90% {
            opacity: 0.5;
          }
          100% {
            transform: translateY(-100vh) translateX(100px);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// MENU BUTTON COMPONENT
// ============================================================================

function MenuButton({ children, onClick, isHovered, onHover, onLeave, primary, disabled }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      disabled={disabled}
      className={`
        relative px-8 py-4
        text-sm tracking-[0.3em] font-light
        transition-all duration-300 ease-out
        border
        ${disabled ? 'opacity-50 cursor-wait' : ''}
        ${primary
          ? 'border-cyan-400/50 text-cyan-400 bg-cyan-400/5'
          : 'border-gray-700 text-gray-400 bg-transparent'
        }
        ${isHovered
          ? 'border-cyan-400 text-white bg-cyan-400/10 tracking-[0.4em]'
          : ''
        }
      `}
      style={{
        boxShadow: isHovered
          ? '0 0 30px rgba(96, 255, 208, 0.2), inset 0 0 30px rgba(96, 255, 208, 0.05)'
          : 'none',
      }}
    >
      {/* Hover glow effect */}
      <div
        className={`
          absolute inset-0 transition-opacity duration-300
          ${isHovered ? 'opacity-100' : 'opacity-0'}
        `}
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(96, 255, 208, 0.1), transparent)',
        }}
      />

      {/* Corner accents */}
      <div className={`absolute top-0 left-0 w-2 h-2 border-t border-l transition-colors duration-300 ${isHovered ? 'border-cyan-400' : 'border-transparent'}`} />
      <div className={`absolute top-0 right-0 w-2 h-2 border-t border-r transition-colors duration-300 ${isHovered ? 'border-cyan-400' : 'border-transparent'}`} />
      <div className={`absolute bottom-0 left-0 w-2 h-2 border-b border-l transition-colors duration-300 ${isHovered ? 'border-cyan-400' : 'border-transparent'}`} />
      <div className={`absolute bottom-0 right-0 w-2 h-2 border-b border-r transition-colors duration-300 ${isHovered ? 'border-cyan-400' : 'border-transparent'}`} />

      <span className="relative z-10">{children}</span>
    </button>
  );
}
